import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import { Button, ConfirmDialog, EmptyState, ErrorState, InputText, Modal, Select, Spinner, Table, Tag } from '../../components'
import type { TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { getErrorCode } from '../../lib/errorCode'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles } from '../admin/AdminPage'

type SlotList = RouterOutputs['configuration']['chillSlots']['list']
type Slot = SlotList['slots'][number]
type Station = SlotList['stations'][number]

/** The API's caps (`configuration.trpc.ts`, the CHECKs of `0021`). */
const CHILLER_MAX_LENGTH = 40
const NAME_MAX_LENGTH = 60

const removeErrorMessage = (error: unknown): string =>
  getErrorCode(error) === 'CONFLICT' ? 'Este hueco tiene lotes: desactívalo en vez de borrarlo.' : trpcMessage(error)

const saveErrorMessage = (error: unknown): string =>
  getErrorCode(error) === 'CONFLICT' ? 'Esa estación ya es un hueco.' : trpcMessage(error)

/** The chiller a station belongs to, read off its title: «Abatidor Hiber Hueco 6» → «Hiber». */
const chillerOf = (title: string): string => {
  const named = /^Abatidor\s+(\S+)/.exec(title)
  return (named?.[1] ?? title.replace(/\s*\(.*$/, '')).slice(0, CHILLER_MAX_LENGTH)
}

/** `ids` with the one at `index` swapped with its neighbour: the order a «Subir» or «Bajar» sends. */
const swapped = (ids: Array<string>, index: number, neighbour: number): Array<string> =>
  ids.map((id, position) => {
    if (position === index) {
      return ids[neighbour]
    }
    return position === neighbour ? ids[index] : id
  })

/**
 * Administración → Racionado → Huecos de abatir: the stations of MRPeasy the
 * hot board fills, one batch at a time each, in the board's order. A new
 * slot is a chiller or tunnel station of the mirror; a slot with batches is
 * deactivated, never deleted.
 */
export const ChillSlotsPage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Slot | null>(null)
  const [deleting, setDeleting] = useState<Slot | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const slotsQuery = trpc.configuration.chillSlots.list.useQuery()

  const afterWrite = (): void => {
    setActionError(null)
    void utils.configuration.chillSlots.list.invalidate()
  }

  const reorder = trpc.configuration.chillSlots.reorder.useMutation({
    onSuccess: afterWrite,
    onError: (error) => setActionError(trpcMessage(error)),
  })
  const toggle = trpc.configuration.chillSlots.save.useMutation({
    onSuccess: afterWrite,
    onError: (error) => setActionError(saveErrorMessage(error)),
  })
  const remove = trpc.configuration.chillSlots.remove.useMutation({
    onSuccess: () => {
      setDeleting(null)
      afterWrite()
    },
    onError: (error) => {
      setDeleting(null)
      setActionError(removeErrorMessage(error))
    },
  })
  const busy = reorder.isPending || toggle.isPending

  const columnsFor = (slots: Array<Slot>): Array<TableColumn<Slot>> => {
    const ids = slots.map((slot) => slot.id)
    return [
      { key: 'order', header: 'Orden', align: 'right', render: (slot) => <span className={styles['admin-page__number']}>{ids.indexOf(slot.id) + 1}</span> },
      {
        key: 'name',
        header: 'Hueco',
        render: (slot) => (
          <span className={styles['admin-page__tags']}>
            <strong>{slot.name}</strong>
            {slot.active ? null : <Tag tone="neutral" label="inactivo" />}
          </span>
        ),
      },
      { key: 'chiller', header: 'Abatidor', render: (slot) => slot.chiller },
      { key: 'station', header: 'Estación', hideOnMobile: true, render: (slot) => `${slot.stationId} · tipo ${slot.typeId}` },
      {
        key: 'active',
        header: 'Activo',
        render: (slot) => (
          <input
            type="checkbox"
            className={styles['admin-page__checkbox']}
            checked={slot.active}
            disabled={isReadOnly || busy}
            aria-label={`${slot.name} activo`}
            onChange={() => toggle.mutate({ id: slot.id, stationId: slot.stationId, chiller: slot.chiller, name: slot.name, active: !slot.active })}
          />
        ),
      },
      {
        key: 'actions',
        header: 'Acciones',
        align: 'right',
        render: (slot) => {
          const index = ids.indexOf(slot.id)
          return (
            <div className={styles['admin-page__row-actions']}>
              <Button
                variant="secondary"
                disabled={isReadOnly || busy || index === 0}
                onClick={() => reorder.mutate({ ids: swapped(ids, index, index - 1) })}
                aria-label={`Subir ${slot.name}`}
                label="Subir"
              />
              <Button
                variant="secondary"
                disabled={isReadOnly || busy || index === ids.length - 1}
                onClick={() => reorder.mutate({ ids: swapped(ids, index, index + 1) })}
                aria-label={`Bajar ${slot.name}`}
                label="Bajar"
              />
              <Button variant="secondary" disabled={isReadOnly} onClick={() => setEditing(slot)} aria-label={`Editar ${slot.name}`} label="Editar" />
              <Button variant="danger" disabled={isReadOnly} onClick={() => setDeleting(slot)} aria-label={`Eliminar ${slot.name}`} label="Eliminar" />
            </div>
          )
        },
      },
    ]
  }

  let body: ReactElement
  if (slotsQuery.isPending) {
    body = <Spinner label="Cargando huecos…" />
  } else if (slotsQuery.isError) {
    body = <ErrorState error={slotsQuery.error} onRetry={() => void slotsQuery.refetch()} />
  } else if (slotsQuery.data.slots.length === 0) {
    body = (
      <EmptyState
        title="No hay huecos"
        message="Sin huecos el tablero de caliente no tiene dónde abatir."
        action={<Button disabled={isReadOnly} onClick={() => setCreating(true)} label="Añadir el primero" />}
      />
    )
  } else {
    const { slots } = slotsQuery.data
    body = <Table columns={columnsFor(slots)} rows={slots} rowKey={(slot) => slot.id} caption="Huecos de abatir, en el orden del tablero" />
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Huecos de abatir"
        intro="Cada hueco es una estación de MRPeasy que abate un lote a la vez. El tablero de caliente los pinta y los llena en este orden."
        actions={(
          <>
            {slotsQuery.isSuccess ? <FreshnessBadge freshness={slotsQuery.data.freshness} /> : null}
            <Button disabled={isReadOnly} onClick={() => setCreating(true)} label="Nuevo hueco" />
          </>
        )}
      />
      {isReadOnly ? <ReadOnlyNote /> : null}
      {actionError !== null ? <p role="alert" className={styles['admin-page__error']}>{actionError}</p> : null}

      {body}

      {creating && slotsQuery.isSuccess ? (
        <SlotDialog
          stations={slotsQuery.data.stations}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            afterWrite()
          }}
        />
      ) : null}
      {editing !== null ? (
        <SlotDialog
          slot={editing}
          stations={[]}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            afterWrite()
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        variant="danger"
        title="Eliminar hueco"
        message={deleting === null ? '' : `«${deleting.name}» deja de estar en el tablero. Si ya tiene lotes, desactívalo en vez de borrarlo.`}
        confirmLabel="Eliminar"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate({ id: deleting.id })
          }
        }}
      />
    </section>
  )
}

type SlotDialogProps = {
  /** The slot being edited; absent to create one, last in the order. */
  slot?: Slot
  /** The mirror's stations no slot uses yet: what a new slot can be. */
  stations: Array<Station>
  onClose: () => void
  onSaved: () => void
}

const SlotDialog = ({ slot, stations, onClose, onSaved }: SlotDialogProps): ReactElement => {
  const [stationId, setStationId] = useState(slot === undefined ? '' : String(slot.stationId))
  const [chiller, setChiller] = useState(slot?.chiller ?? '')
  const [name, setName] = useState(slot?.name ?? '')
  const [active, setActive] = useState(slot?.active ?? true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const save = trpc.configuration.chillSlots.save.useMutation({ onSuccess: onSaved, onError: (error) => setErrorMessage(saveErrorMessage(error)) })

  const pick = (value: string): void => {
    setStationId(value)
    const station = stations.find((candidate) => String(candidate.stationId) === value)
    if (station !== undefined) {
      setName(station.title.slice(0, NAME_MAX_LENGTH))
      setChiller(chillerOf(station.title))
    }
  }

  const canSubmit = stationId !== '' && chiller.trim() !== '' && name.trim() !== '' && !save.isPending

  return (
    <Modal open title={slot === undefined ? 'Nuevo hueco' : `Editar ${slot.name}`} onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (canSubmit) {
            setErrorMessage(null)
            save.mutate({ id: slot?.id, stationId: Number(stationId), chiller: chiller.trim(), name: name.trim(), active })
          }
        }}
      >
        {slot === undefined ? (
          stations.length === 0 ? (
            <p className={styles['admin-page__note']}>Todas las estaciones de abatir y de túnel del espejo ya son huecos.</p>
          ) : (
            <Select
              label="Estación de MRPeasy"
              placeholder="Elige una estación…"
              options={stations.map((station) => ({ value: String(station.stationId), label: `${station.title} · ${station.code} · tipo ${station.typeId}` }))}
              value={stationId}
              onChange={(event) => pick(event.target.value)}
            />
          )
        ) : (
          <p className={styles['admin-page__muted']}>{`Estación ${slot.stationId} · tipo ${slot.typeId}`}</p>
        )}
        <InputText label="Abatidor" hint="El aparato al que pertenece: Infrico, Hiber, Túnel…" value={chiller} maxLength={CHILLER_MAX_LENGTH} onChange={(event) => setChiller(event.target.value)} />
        <InputText label="Nombre del hueco" value={name} maxLength={NAME_MAX_LENGTH} onChange={(event) => setName(event.target.value)} />
        <label className={styles['admin-page__toggle']}>
          <input type="checkbox" className={styles['admin-page__checkbox']} checked={active} onChange={(event) => setActive(event.target.checked)} />
          Activo: el tablero lo usa
        </label>
        {errorMessage !== null ? <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p> : null}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending} label="Cancelar" />
          <Button type="submit" disabled={!canSubmit} label={slot === undefined ? 'Crear hueco' : 'Guardar cambios'} />
        </div>
      </form>
    </Modal>
  )
}
